import {
  SHStack,
  SHToggleButtonOptions,
  SHTypography,
} from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { FeeExportDTO } from "@models/platform-analysis/entities/feeExport";
import {
  AnalysisFeeContainerDTO,
  AnalysisStepFeeDTO,
  FeeSubProductDTO,
} from "@models/platform-analysis/entities/steps/fee";
import {
  AnalysisSummaryDTO,
  ProductRecommendationDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { Collapse } from "@mui/material";
import { getProductRecommendationConfigs } from "@pages/platform-analysis-v2/_id/steps/summary/utils";
import {
  removeSubProductNameForSpecificProduct,
  sortBySubProductOrder,
} from "@pages/platform-analysis-v2/_id/utils";
import { ExportExcelButton } from "@pages/platform-analysis/components/buttons/export-excel";
import { ChartLegendBox } from "@pages/platform-analysis/_id/steps/fee/analysis/components/chart-legend-box";
import { FeeHeatMapV2 } from "@pages/platform-analysis/_id/steps/fee/analysis/components/heatmap-v2";
import { FeeLineChart } from "@pages/platform-analysis/_id/steps/fee/analysis/components/line-chart";
import {
  HeatMapTableTitleConstants,
  LineChartLegendConstants,
} from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import { RootState } from "@redux/store";
import { debounce, isEqual } from "lodash";
import { ComponentProps, memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

interface FeeChartSectionProps {
  isAppendix?: boolean;
  allowHideChart?: boolean;
  hideChart?: boolean;
  disabled?: boolean;
  heatmapProps?: Partial<ComponentProps<typeof FeeHeatMapV2>>;
  feeLineChartProps?: Partial<ComponentProps<typeof FeeLineChart>>;
  onChartVisibilityChange?: (hideChart: boolean) => void;
  getTotalPortfolioValue?: (state: RootState) => number | undefined | null;
  getAnalysisStepFeeData?: (
    state: RootState,
  ) => AnalysisStepFeeDTO | AnalysisSummaryDTO | undefined | null;
  getContainerData?: (
    state: RootState,
  ) => AnalysisFeeContainerDTO | undefined | null;
  getSubProductsFiltered?: (state: RootState) => FeeSubProductDTO[];
  getHiddenIdsIndexMap?: (state: RootState) => boolean[] | undefined;
  getHiddenIds?: (state: RootState) => string[] | undefined;
  getStoredProductRecommendations?: (
    state: RootState,
  ) => Record<string, Partial<ProductRecommendationDTO>> | undefined;
  onChangeHiddenIds: ((hiddenIds: string[]) => void) | undefined;
}

export const FeeChartSection = memo(
  ({
    isAppendix = false,
    hideChart,
    allowHideChart = false,
    heatmapProps,
    feeLineChartProps,
    disabled,
    onChartVisibilityChange,
    onChangeHiddenIds,
    getTotalPortfolioValue = (state: RootState) =>
      state.platformAnalysis?.platformAnalysis?.fee?.feePortfolioDetails
        ?.totalPortfolioValue,
    getAnalysisStepFeeData = (state: RootState) =>
      state.platformAnalysis?.platformAnalysis?.fee,
    getContainerData = (state: RootState) =>
      state.platformAnalysis?.platformAnalysis?.fee?.all,
    getSubProductsFiltered = (state: RootState) =>
      state.platformAnalysis.internal.feeAnalysis.subProductFiltered,
    getHiddenIdsIndexMap = (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.hiddenIdsIndexMap,
    getHiddenIds = (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.hiddenIds,
    getStoredProductRecommendations = () => undefined,
  }: FeeChartSectionProps) => {
    // #region Hooks
    const { portfolioLevel, platformAnalysis, isCompleted } =
      usePlatformAnalysis();
    const {
      feeAnalysis: { displayStyle, heatmapMode, performanceScope },
    } = useSelector((state: RootState) => state.platformAnalysis.internal);

    const totalPortfolioValue = useSelector(getTotalPortfolioValue);
    const stepFeeData = useSelector(getAnalysisStepFeeData);
    const containerData = useSelector(getContainerData);
    const subProductFiltered = useSelector(getSubProductsFiltered);
    const hiddenIdsIndexMap = useSelector(getHiddenIdsIndexMap);
    const hiddenIds = useSelector(getHiddenIds);
    const storedProductRecommendations = useSelector(
      getStoredProductRecommendations,
    );
    // #endregion Hooks

    const [recommendationStatusColorMap, setRecommendationStatusColorMap] =
      useState<Record<string, string>>({});

    // #region States & Refs
    const isSummaryStep = useMemo(
      () => platformAnalysis?.currentStep === AnalysisStep.Summary,
      [platformAnalysis],
    );

    const isFamilyLevel = useMemo(
      () => isEqual(portfolioLevel, FeeCalculatorPortfolioLevel.Family),
      [portfolioLevel],
    );

    const isShowFeeDetail = useMemo(
      () => isEqual(heatmapMode, FeeHeatmapMode.Detail),
      [heatmapMode],
    );

    const isExcludePerformanceFees = useMemo(
      () => isEqual(performanceScope, FeesPerformanceScope.Excluding),
      [performanceScope],
    );

    const subProducts = useMemo(() => {
      const orderedSubProduct = sortBySubProductOrder({
        data: subProductFiltered,
        getProductId: (item) => item.productId,
        productRecommendations: storedProductRecommendations,
      });

      return removeSubProductNameForSpecificProduct(orderedSubProduct ?? []);
    }, [storedProductRecommendations, subProductFiltered]);

    const subProductsDataForExcel = useMemo(() => {
      return subProductFiltered.filter(
        (subProduct) => !hiddenIds?.includes(subProduct.id),
      );
    }, [subProductFiltered, hiddenIds]);
    // #endregion

    // #region Functions
    const debounceUpdateRecommendationStatusColorMap = debounce(() => {
      if (!storedProductRecommendations) return;

      const colorMap: Record<string, string> = {};

      subProducts?.forEach((item) => {
        const recommendation = storedProductRecommendations[item.productId];
        if (!recommendation || !recommendation?.recommendationType) return;
        const configs = getProductRecommendationConfigs(recommendation);
        if (!configs?.backgroundColor) return;
        colorMap[item.id] = configs.backgroundColor;
      });

      setRecommendationStatusColorMap(colorMap);
    }, 300);
    // #endregion Functions

    // #region Effects
    useEffect(() => {
      debounceUpdateRecommendationStatusColorMap();

      return () => {
        debounceUpdateRecommendationStatusColorMap.cancel();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedProductRecommendations, subProducts]);
    // #endregion Effects

    // #region Render
    return (
      <SHStack
        gap={2}
        sx={{
          ...Object.fromEntries(
            subProducts?.map((item) => {
              return [
                `& .FeeChartSection-heatmap-sub-product-${item.productId}`,
                {
                  position: "relative",
                  ":before": {
                    content: '""',
                    position: "absolute",
                    width: "5px",
                    left: "-10px",
                    top: "5%",
                    bottom: "5%",
                    borderStartStartRadius: "4px",
                    borderEndStartRadius: "4px",
                    backgroundColor: recommendationStatusColorMap[item.id],
                    transition: "background-color 0.3s ease-in-out",
                  },
                },
              ];
            }) ?? [],
          ),
        }}
      >
        <FeeHeatMapV2
          subProducts={subProducts}
          totalPortfolioValue={totalPortfolioValue ?? undefined}
          mode={displayStyle}
          heatmapMode={heatmapMode}
          hiddenIds={hiddenIds}
          hiddenIdIndex={hiddenIdsIndexMap ?? []}
          onChangeHiddenIds={onChangeHiddenIds}
          isCompleted={isCompleted}
          isExcludePerformanceFees={isExcludePerformanceFees}
          titleComp={
            <SHStack
              className="FeeChartSection-heatmap-title"
              direction={"row"}
              spacing={"5px"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <SHTypography variant="subtitle1" fontWeight={700}>
                {isFamilyLevel
                  ? heatmapMode !== FeeHeatmapMode.Projection
                    ? performanceScope === FeesPerformanceScope.Including
                      ? HeatMapTableTitleConstants.DetailViewTitle
                      : HeatMapTableTitleConstants.DetailViewExcPerfTitle
                    : performanceScope === FeesPerformanceScope.Including
                    ? HeatMapTableTitleConstants.ProjectionViewTitle
                    : HeatMapTableTitleConstants.ProjectionViewExcPerfTitle
                  : HeatMapTableTitleConstants.DefaultTitle}
              </SHTypography>

              {containerData?.subProducts && (
                <ExportExcelButton
                  disabled={disabled}
                  mode={displayStyle}
                  isExcludePerformanceFees={isExcludePerformanceFees}
                  data={
                    {
                      ...stepFeeData,
                      originalSubProduct: containerData?.subProducts,
                      hiddenIds: hiddenIds,
                      hiddenIndexColumnMap: hiddenIdsIndexMap,
                      subProducts: subProductsDataForExcel,
                      databaseVersion:
                        platformAnalysis?.databaseVersion ?? null,
                      isShowHoldingsNumber: containerData?.isShowHoldingsNumber,
                      portfolioSetup: containerData?.portfolioSetup,
                      smaPortfolioFee: isAppendix
                        ? stepFeeData?.disqualifiedSmaPortfolioFee
                        : stepFeeData?.smaPortfolioFee,
                      portfolioCompositionByPlatform: isAppendix
                        ? stepFeeData?.disqualifiedPortfolioCompositionByPlatform
                        : stepFeeData?.portfolioCompositionByPlatform,
                    } as FeeExportDTO
                  }
                />
              )}
            </SHStack>
          }
          bottomLegend={
            <ChartLegendBox>
              {isShowFeeDetail ? "Fee type" : LineChartLegendConstants.XAxis}
            </ChartLegendBox>
          }
          isSummaryStep={isSummaryStep}
          {...heatmapProps}
        />

        <SHStack>
          {allowHideChart && (
            <SHStack
              width={"100%"}
              justifyContent={"flex-end"}
              alignItems={"end"}
            >
              <SHStack width={"max-content"}>
                <SHToggleButtonOptions
                  disabled={disabled}
                  options={[
                    { label: "Show", value: "false" },
                    { label: "Hide", value: "true" },
                  ]}
                  value={hideChart?.toString()}
                  onChange={(_, value) => {
                    if (!value) return;
                    onChartVisibilityChange?.(value === "true");
                  }}
                />
              </SHStack>
            </SHStack>
          )}

          <Collapse
            in={!hideChart}
            sx={{
              "& .FeeSubProductCard-productName": {
                fontWeight: "bold !important",
              },
              "& .FeeSubProductCard-subProductName": {
                fontWeight: "normal !important",
              },
            }}
          >
            <FeeLineChart
              subProducts={subProducts}
              mode={displayStyle}
              hiddenIds={hiddenIds}
              onChangeHiddenIds={onChangeHiddenIds}
              title={
                isFamilyLevel
                  ? performanceScope === FeesPerformanceScope.Including
                    ? HeatMapTableTitleConstants.ProjectionViewTitle
                    : HeatMapTableTitleConstants.ProjectionViewExcPerfTitle
                  : HeatMapTableTitleConstants.DefaultTitle
              }
              yAxisLegend={
                isFamilyLevel
                  ? LineChartLegendConstants.YAxisPlatformAndSMAFees
                  : LineChartLegendConstants.YAxisPlatformFees
              }
              isExcludePerformanceFees={
                performanceScope === FeesPerformanceScope.Excluding
              }
              {...feeLineChartProps}
            />
          </Collapse>
        </SHStack>
      </SHStack>
    );
    // #endregion Render
  },
);
